import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Input from 'src/components/Input/Input';
import { getSubdomainHelper } from 'src/helpers/getSubdomainHelper';
import { clsx } from 'clsx';
import { Button } from 'src/components/ui/button';
import { Spinner } from 'src/components/Spinner/Spinner';
import ArrowLargeIcon from 'src/assets/img/arrow-large-icon.svg';

export const EditZendeskChatApiDetailsSettings = ({
  isAuthorized,
  isZendeskOAuthUsed,
  isCheckingAuthStatus,
  onConnectWithZendesk,
  onReconnectWithZendesk,
}: {
  isAuthorized: boolean;
  isZendeskOAuthUsed: boolean;
  isCheckingAuthStatus: boolean;
  onConnectWithZendesk: () => void;
  onReconnectWithZendesk: () => void;
}) => {
  const { control } = useFormContext();
  return (
    <div className={'w-full flex flex-col gap-2.5 p-2.5 bg-[#A498FF1A] rounded-sm-5'}>
      {isCheckingAuthStatus && (
        <div
          className={
            'absolute flex items-center justify-center w-full h-full top-0 left-0 bg-ebony_opacity_30 backdrop-blur-sm z-50'
          }
        >
          <div
            className={
              'flex flex-col items-center gap-4 bg-light p-4 rounded-sm-5 text-sm font-semibold'
            }
          >
            <Spinner className={'!w-4 !border-[3px] !border-r-ebony_opacity_50'} />
            <p>Checking Zendesk Connection...</p>
          </div>
        </div>
      )}
      <div>
        <Controller
          control={control}
          name="subdomain"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              type={'text'}
              label={'Zendesk subdomain'}
              placeholder={'domain.com'}
              invalidMessage={error?.message}
              value={value}
              isValid={!error?.message}
              onChange={(e) => {
                const res = getSubdomainHelper(e.target.value);
                if (res?.subdomain) {
                  onChange(res?.subdomain);
                } else {
                  onChange(e.target.value);
                }
              }}
              infoTooltip={{ side: 'right', description: 'Link to your Zendesk workspace.' }}
            />
          )}
        />
      </div>
      <div>
        <Controller
          control={control}
          name="app_id"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              type={'text'}
              label={'App ID'}
              placeholder={'654321'}
              invalidMessage={error?.message}
              value={value}
              isValid={!error?.message}
              onChange={onChange}
              infoTooltip={{
                side: 'right',
                description: (
                  <>
                    Unique numeric identifier assigned to each
                    <br /> app installed in your Zendesk account.
                  </>
                ),
              }}
            />
          )}
        />
      </div>
      <div>
        <Controller
          control={control}
          name="conversation_key_id"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              type={'text'}
              label={'Key ID'}
              placeholder={'abc123def456ghi789jkl0'}
              invalidMessage={error?.message}
              value={value}
              isValid={!error?.message}
              onChange={onChange}
              infoTooltip={{
                side: 'right',
                description: (
                  <>
                    Used in JWT authentication to identify
                    <br /> the key used to sign a token.
                  </>
                ),
              }}
            />
          )}
        />
      </div>
      <div>
        <Controller
          control={control}
          name="conversation_secret_key"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              type={'text'}
              label={'Secret key'}
              placeholder={'x9Xb7gFQ2ZktYp3LqHNdMvuRJW5kq8Eo'}
              invalidMessage={error?.message}
              value={value}
              isValid={!error?.message}
              onChange={onChange}
              infoTooltip={{
                side: 'right',
                description: (
                  <>
                    Confidential string used with your OAuth client to
                    <br /> securely authenticate your app with Zendesk's API.
                  </>
                ),
              }}
            />
          )}
        />
      </div>
      <div className={'mt-2.5 flex flex-col gap-2'}>
        <Button
          type={'button'}
          size={'sm'}
          onClick={onConnectWithZendesk}
          disabled={isZendeskOAuthUsed && isAuthorized}
          className={clsx(
            'w-full gap-2 px-8',
            isZendeskOAuthUsed && isAuthorized
              ? 'bg-purple_150 text-purple_600 font-medium shadow-none disabled:opacity-100'
              : 'font-semibold bg-ebony text-white hover:bg-ebony_opacity_80 shadow-[0_1px_1px_0_rgba(5,5,13,0.25)]',
          )}
        >
          {isZendeskOAuthUsed && isAuthorized ? (
            'Connected successfully with Zendesk'
          ) : (
            <>
              Connect with Zendesk
              <img src={ArrowLargeIcon} alt={''} width={16} height={16} />
            </>
          )}
        </Button>
        {isZendeskOAuthUsed && isAuthorized && (
          <Button
            type={'button'}
            size={'sm'}
            onClick={onReconnectWithZendesk}
            className={
              'w-full gap-2 px-8 font-semibold bg-ebony text-white hover:bg-ebony_opacity_80 shadow-[0_1px_1px_0_rgba(5,5,13,0.25)]'
            }
          >
            Reconnect with Zendesk
            <img src={ArrowLargeIcon} alt={''} width={16} height={16} />
          </Button>
        )}
        <p className={'text-center text-[13px] font-medium text-tertiary'}>
          You’ll be redirected to Zendesk to authorize access.
        </p>
      </div>
    </div>
  );
};
